import { BaseTransport } from '../transports'
import { RequestUserConfig } from '../common/types'
import { NetworkService } from '../services'
import {
    AvailabilityResponseTransport,
    AvailabilityCalendarResponseTransport,
} from '../transports/availability-transport'
import RegionPriceAndAvailabilityTransport from '../transports/region-price-and-availability-transport'

class AvailabilityClient {
    public getAvailableArrivalDates({
        user,
        params,
        signal,
    }: {
        user: RequestUserConfig
        params: any
        signal?: AbortSignal | null
    }): Promise<BaseTransport<AvailabilityResponseTransport>> {
        const { listingId, month, duration } = params
        const headers = {
            'x-language': user.language,
            'x-domain': user.domain,
            ...(user.session?.id && { 'x-session-id': user.session.id }),
        }

        return NetworkService.get({
            url: `/api/v1/availability/listings/${listingId}/arrivals/${month}`,
            headers,
            params: { duration },
            restConfig: { signal },
        })
    }

    public getAvailableDepartureDates({
        user,
        params,
        signal,
    }: {
        user: RequestUserConfig
        params: any
        signal: AbortSignal | null
    }): Promise<BaseTransport<AvailabilityResponseTransport>> {
        const { listingId, arrivalDate, month, duration } = params
        const headers = {
            'x-language': user.language,
            'x-domain': user.domain,
            ...(user.session?.id && { 'x-session-id': user.session.id }),
        }

        return NetworkService.get({
            url: `/api/v1/availability/listings/${listingId}/departures/${arrivalDate}/${month}`,
            headers,
            params: { duration },
            restConfig: { signal },
        })
    }

    public getCalendarAvailability({
        user,
        listingId,
    }: {
        user: RequestUserConfig
        listingId: string
    }): Promise<BaseTransport<AvailabilityCalendarResponseTransport>> {
        const headers = {
            'x-language': user.language,
            'x-domain': user.domain,
            ...(user.session?.id && { 'x-session-id': user.session.id }),
        }

        return NetworkService.get({
            url: `/api/v1/availability/listings/${listingId}`,
            headers,
        })
    }

    public getBulkValidatePeriod({
        user,
        arrivalDate,
        departureDate,
        listingIds,
        signal,
    }: {
        user: RequestUserConfig
        arrivalDate: string
        departureDate: string
        listingIds: number[]
        signal?: AbortSignal | null
    }): Promise<BaseTransport<Record<string, boolean>>> {
        const headers = {
            'x-language': user.language,
            'x-domain': user.domain,
            ...(user.session?.id && { 'x-session-id': user.session.id }),
        }

        return NetworkService.post({
            url: `/api/v1/availability/listings/bulk/validate/period/${arrivalDate}/${departureDate}`,
            headers,
            body: { listingIds },
            restConfig: { signal },
        })
    }

    public getRegionPriceAndAvailability({
        user,
        requestPath,
    }: {
        user: RequestUserConfig
        requestPath: string
    }): Promise<BaseTransport<RegionPriceAndAvailabilityTransport>> {
        const headers = {
            'x-language': user.language,
            'x-domain': user.domain,
            ...(user.session?.id && { 'x-session-id': user.session.id }),
        }

        return NetworkService.get({
            url: '/api/v1/region-price-and-availability',
            headers,
            params: { requestPath },
        })
    }
}

export default new AvailabilityClient()
